import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { VerifiedBadgeModal } from "@/components/VerifiedBadgeModal";
import { Button } from "@/components/ui/button";
import { ShieldCheck, X, Check, Hourglass, IdCard } from "lucide-react";
import { isCreatorCategory, stepBenefits } from "@/lib/privileges";

const ROLE_FB = { preacher: "Prédicateur", artist: "Artiste", influencer: "Influenceur(e)" };

// Which reminder a creator needs — driven by the backend resolver (user.privileges = rules from Admin › Privilèges):
// role pending → neutral ; badge missing AND the badge would unlock something → badge ; identity missing AND it would
// unlock something → identity ; otherwise nothing. No rule is hard-coded here.
export const reminderState = (user, t = (k, f) => f) => {
  const p = user?.privileges;
  if (!user || !p) return null;
  if (p.role_pending) return "role_pending";
  if (!isCreatorCategory(p.category)) return null;
  if (!p.badge && stepBenefits(p, "badge", t).unlocks) return "badge";
  if (!p.identity_verified && stepBenefits(p, "identity", t).unlocks) return "identity";
  return null;
};

const Shell = ({ icon: Icon, title, body, children, onDismiss, testId, state }) => (
  <div className="yalola-card p-4 flex items-start gap-3" style={{ border: "1px solid var(--yalola-primary)", background: "var(--yalola-primary-soft)" }} data-testid={testId} data-state={state}>
    <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--yalola-primary)", color: "#fff" }}><Icon size={20} /></div>
    <div className="min-w-0 flex-1">
      <div className="font-semibold font-display text-[15px]">{title}</div>
      <p className="text-sm mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{body}</p>
      {children}
    </div>
    <button onClick={onDismiss} className="shrink-0 opacity-60 hover:opacity-100 transition-opacity" data-testid="verify-reminder-dismiss" aria-label="Fermer"><X size={16} /></button>
  </div>
);

const Benefits = ({ items }) => items.length > 0 && (
  <ul className="mt-2 space-y-1" data-testid="verify-reminder-benefits">
    {items.map((b, i) => (
      <li key={i} className="flex items-center gap-1.5 text-[13px]" style={{ color: "var(--yalola-text)" }}><Check size={14} style={{ color: "var(--yalola-primary)" }} className="shrink-0" /><span>{b}</span></li>
    ))}
  </ul>
);

const goalText = (money, t) => {
  const alone = new Set(money.filter((m) => !m.withOther).map((m) => m.key));
  if (alone.has("offerings") && alone.has("withdraw")) return t("verify.goal_both", "recevoir des offrandes et dons et retirer tes gains");
  if (alone.has("offerings")) return t("verify.goal_offerings", "recevoir des offrandes et dons");
  if (alone.has("withdraw")) return t("verify.goal_withdraw", "retirer tes gains");
  return null;
};

export const VerifyAccountReminder = () => {
  const { user, t, config, lang } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const state = dismissed ? null : reminderState(user, t);
  if (!state) return null;
  const p = user.privileges;
  const roleKey = state === "role_pending" ? user.requested_role : user.role;
  const roleLabel = t(`role.${roleKey}`, ROLE_FB[roleKey] || "créateur");
  const dismiss = () => setDismissed(true);

  if (state === "role_pending") {
    return (
      <Shell icon={Hourglass} state={state} testId="verify-account-reminder" onDismiss={dismiss}
        title={t("verify.role_pending_title", "Demande de rôle {role} en cours d'examen").replace("{role}", roleLabel)}
        body={t("verify.role_pending_body", "En attendant la décision de notre équipe, tu utilises Yalola comme un fidèle. Tu seras prévenu(e) dès qu'elle sera prise.")}>
        {user.kyc_status === "info_requested" && (
          <Button size="sm" className="pill-primary rounded-full mt-2.5" onClick={() => setOpen(true)} data-testid="verify-reminder-complete-btn">{t("verify.complete_cta", "Compléter ma demande")}</Button>
        )}
        <VerifiedBadgeModal open={open} onOpenChange={setOpen} defaultType={user.requested_role} />
      </Shell>
    );
  }
  if (state === "identity") {
    const { lines, money } = stepBenefits(p, "identity", t);
    const goal = goalText(money, t);
    const body = `${p.badge ? t("verify.identity_badge_active", "Ton badge Yalola Vérifié est actif.") + " " : ""}${goal
      ? t("verify.identity_body_goal", "Pour {goal}, il ne manque plus que ta vérification d'identité.").replace("{goal}", goal)
      : t("verify.identity_body_generic", "Ta vérification d'identité débloque de nouveaux avantages :")}`;
    return (
      <Shell icon={IdCard} state={state} testId="verify-account-reminder" onDismiss={dismiss} title={t("verify.identity_title", "Vérifie ton identité")} body={body}>
        <Benefits items={[...lines, ...(goal ? [] : money.map((m) => m.text))]} />
        <Button size="sm" className="pill-primary rounded-full mt-2.5" onClick={() => navigate(`/profile/${user.id}?tab=espace&identity=open#identite`)} data-testid="verify-reminder-identity-btn">
          <IdCard size={14} className="mr-1" /> {t("verify.identity_cta", "Vérifier mon identité")}
        </Button>
      </Shell>
    );
  }
  const msg = config?.privileges?.messages?.[lang] || {};
  const { lines, money } = stepBenefits(p, "badge", t);
  const needsIdentityToo = money.some((m) => m.withOther);
  return (
    <Shell icon={ShieldCheck} state={state} testId="verify-account-reminder" onDismiss={dismiss}
      title={(msg.banner_title || t("verify.badge_title", "Active Yalola Vérifié, {role}")).replace("{role}", roleLabel)}
      body={(msg.banner_body || t("verify.badge_body", "Ton rôle {role} est accordé. Avec Yalola Vérifié, tu débloques tes privilèges premium :")).replace("{role}", roleLabel)}>
      <Benefits items={[...lines, ...money.map((m) => m.text)]} />
      <Button size="sm" className="pill-primary rounded-full mt-2.5" onClick={() => setOpen(true)} data-testid="verify-reminder-btn">
        <ShieldCheck size={14} className="mr-1" /> {t("verify.badge_cta", "Activer Yalola Vérifié")}
      </Button>
      {needsIdentityToo && <p className="text-[11px] mt-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="verify-reminder-identity-hint">{t("verify.badge_then_identity_generic", "Certains avantages demandent aussi ton identité vérifiée (Profil › Mon espace).")}</p>}
      <VerifiedBadgeModal open={open} onOpenChange={setOpen} defaultType={user.role} />
    </Shell>
  );
};

export default VerifyAccountReminder;
